import { useEffect, useState } from "react";
import { Phone, MessageCircle, Flame, Sparkles, ScanLine } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import API from "@/lib/api";
import { Reveal, Eyebrow } from "./Reveal";
import { bnNum } from "@/constants/site";
import { useSite } from "@/lib/site";
import { useLang } from "@/lib/lang";

const AMOUNTS = [11, 51, 101, 501, 1101, 2100];

export default function Donate() {
  const SITE = useSite();
  const { lang, t } = useLang();
  const [settings, setSettings] = useState(null);
  const [amount, setAmount] = useState(501);
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState("");

  useEffect(() => {
    API.get("/settings/donation")
      .then(({ data }) => setSettings(data))
      .catch(() => setSettings({}));
  }, []);

  const finalAmount = custom ? Number(custom) || 0 : amount;
  const upiId = (settings?.upi_id || "").trim();
  const upiLink = `upi://pay?pa=${upiId}&pn=${encodeURIComponent("Rajendralal St Durga Puja")}&am=${finalAmount || ""}&cu=INR&tn=${encodeURIComponent("Donation - Puja Fund 2026")}`;
  const waText = encodeURIComponent(
    `নমস্কার! আমি${donor ? ` (${donor})` : ""} পুজো ফান্ডে ₹${finalAmount || ""} অনুদান করেছি — পেমেন্টের স্ক্রিনশট পাঠাচ্ছি।`
  );

  const celebrate = () => {
    confetti({ particleCount: 170, spread: 85, origin: { y: 0.7 }, colors: ["#F2B134", "#D7261E", "#FDF6EA"] });
    toast.success(t("don_thanks"));
  };

  const inputCls = "w-full rounded-xl bg-[#2B0F15] border border-[#F2B134]/20 px-4 py-3 text-[#FDF6EA] placeholder:text-[#D1BBA7]/50 outline-none focus:border-[#F2B134]/70 transition-colors";
  const fmtAmt = (n) => (lang === "bn" ? bnNum(n) : n);

  return (
    <section id="donate" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden frame-gold">
            <img
              src="https://images.unsplash.com/photo-1605378229010-11aedbb01b24?q=80&w=1400&auto=format&fit=crop"
              alt="প্রদীপের আলো"
              className="w-full h-[380px] sm:h-[460px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#120507]/90 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-bengali text-2xl font-bold text-[#FDF6EA]">{t("don_img_t")}</p>
              <p className="font-bengali text-sm text-[#D1BBA7] mt-2">{t("don_img_s")}</p>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <Eyebrow>{t("don_eyebrow")}</Eyebrow>
            <h2 className="font-bengali font-extrabold text-3xl sm:text-5xl mt-4 leading-tight">
              {t("don_title_a")} <span className="gold-text">{t("don_title_b")}</span>
            </h2>
          </Reveal>

          <Reveal delay={0.08} className="mt-8">
            <div className="rounded-3xl border border-[#F2B134]/25 bg-[#1E0A0E] p-6 spotlight" data-testid="donation-anjali-panel">
              <p className="font-bengali font-bold text-lg flex items-center gap-2">
                <Sparkles size={18} className="text-[#F2B134]" /> {t("don_anjali")}
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {AMOUNTS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    data-testid={`donation-amount-chip-${a}`}
                    onClick={() => { setAmount(a); setCustom(""); }}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                      !custom && amount === a ? "btn-gold" : "border border-[#F2B134]/25 text-[#D1BBA7] hover:text-[#F2B134]"
                    }`}
                  >
                    ₹{fmtAmt(a)}
                  </button>
                ))}
                <input
                  inputMode="numeric"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value.replace(/\D/g, "").slice(0, 7))}
                  placeholder={t("don_custom_ph")}
                  data-testid="donation-custom-input"
                  className="w-32 rounded-full bg-[#2B0F15] border border-[#F2B134]/25 px-4 py-2 text-sm text-[#FDF6EA] outline-none focus:border-[#F2B134]/70"
                />
              </div>

              <input
                value={donor}
                onChange={(e) => setDonor(e.target.value)}
                placeholder={t("don_name_ph")}
                data-testid="donation-name-input"
                className={`${inputCls} mt-4 font-bengali`}
              />

              {upiId ? (
                <div className="mt-6 flex flex-col sm:flex-row gap-6 items-center">
                  <div className="rounded-2xl bg-[#FDF6EA] p-4 border-4 border-[#F2B134]/60 shrink-0 text-center" data-testid="donation-qr">
                    <QRCodeSVG value={upiLink} size={168} bgColor="#FDF6EA" fgColor="#1E0A0E" level="M" />
                    <p className="text-[10px] text-[#1E0A0E]/70 mt-2 font-mono">{upiId}</p>
                  </div>
                  <div className="flex-1 space-y-3 w-full">
                    <p className="font-bengali text-sm text-[#D1BBA7] flex items-start gap-2">
                      <ScanLine size={15} className="text-[#F2B134] mt-0.5 shrink-0" />
                      {t("don_scan", { amt: fmtAmt(finalAmount || 0) })}
                    </p>
                    <a
                      href={upiLink}
                      data-testid="donation-upi-pay-btn"
                      className="btn-gold rounded-full px-6 py-3 text-sm font-bold inline-flex items-center gap-2 sm:hidden"
                    >
                      {t("don_pay")}
                    </a>
                    <button type="button" onClick={celebrate} data-testid="donation-confirm-btn" className="btn-crimson rounded-full px-6 py-3 text-sm font-bold w-full sm:w-auto">
                      {t("don_confirm")}
                    </button>
                    <a
                      href={`https://wa.me/91${SITE.whatsapp[0]}?text=${waText}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="donation-whatsapp-confirm"
                      className="block text-xs text-[#D1BBA7] hover:text-[#F2B134] transition-colors"
                    >
                      {t("don_wa")}
                    </a>
                  </div>
                </div>
              ) : (
                <p className="mt-5 font-bengali text-sm text-[#D1BBA7]/80 border-l-2 border-[#F2B134]/40 pl-4">
                  {t("don_fallback")}
                </p>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.12} className="mt-6 space-y-4">
            {SITE.donationPhones.map((ph, i) => (
              <div key={ph} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#F2B134]/20 bg-[#1E0A0E] p-4">
                <span className="h-10 w-10 rounded-full bg-[#D7261E]/15 border border-[#D7261E]/40 grid place-items-center text-[#FFB4AE]">
                  <Flame size={16} />
                </span>
                <div className="flex-1 min-w-[120px]">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#D1BBA7]/70">{i === 0 ? t("don_card1") : t("don_card2")}</p>
                  <p className="font-bold tabular-nums text-[#FDF6EA]">{ph}</p>
                </div>
                <a href={`tel:+91${ph}`} data-testid={`donation-call-${ph}`} className="btn-gold rounded-full px-4 py-2 text-xs font-bold inline-flex items-center gap-2">
                  <Phone size={12} /> {t("don_call")}
                </a>
                <a href={`https://wa.me/91${ph}`} target="_blank" rel="noopener noreferrer" data-testid={`donation-whatsapp-${ph}`} className="btn-crimson rounded-full px-4 py-2 text-xs font-bold inline-flex items-center gap-2">
                  <MessageCircle size={12} /> {t("don_wa_btn")}
                </a>
              </div>
            ))}
          </Reveal>

          <Reveal delay={0.16}>
            <p className="mt-5 font-bengali text-sm text-[#D1BBA7]/70 border-l-2 border-[#F2B134]/40 pl-4">
              {t("don_note")}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
